import { Outlet, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { WHATSAPP_NUMBER, buildWhatsAppLink } from "@/lib/constants";

const Layout = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);

  return (
    <div className="bc-noise relative min-h-screen bg-[#0A0D14] text-[#F6F7FB]">
      {/* Top status bar */}
      <div
        className="hidden md:flex items-center justify-between border-b border-white/5 px-6 lg:px-10 py-2.5 text-[11px] tracking-[0.32em] uppercase text-[#6F7787] relative z-30"
        data-testid="top-status-bar"
      >
        <div className="flex items-center gap-6">
          <span className="flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#58D2C7] bc-pulse" />
            Karnataka, India
          </span>
          <span>Mission · Trust · Service</span>
        </div>
        <div className="flex items-center gap-6">
          <span>v · founding cohort</span>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            className="hover:text-[#D6B25E] transition-colors"
            target="_blank"
            rel="noreferrer"
          >
            +91 96864 54696
          </a>
        </div>
      </div>

      {/* Nav */}
      <header
        className={`sticky top-0 z-40 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-[#0A0D14]/80 border-b border-white/5"
            : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 flex items-center justify-between py-4 md:py-5">
          <Link
            to="/"
            className="flex items-center gap-3 group min-w-0"
            data-testid="brand-logo-link"
          >
            <BrandMark />
            <div className="leading-none min-w-0">
              <div className="font-serif-display text-[18px] sm:text-[22px] tracking-tight truncate">
                Believers <span className="italic text-[#D6B25E]">Common</span>
              </div>
              <div className="text-[9px] sm:text-[10px] tracking-[0.32em] sm:tracking-[0.4em] uppercase text-[#6F7787] mt-1">
                A Trusted Common
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-10 text-[13px] tracking-[0.18em] uppercase">
            <NavLink to="/" current={pathname === "/"} label="Home" testid="nav-home" />
            <NavLink to="/about" current={pathname === "/about"} label="About" testid="nav-about" />
            <a href="/#what-we-do" className="bc-link" data-testid="nav-services">
              Services
            </a>
            <a href="/#how-it-works" className="bc-link" data-testid="nav-how">
              How
            </a>
            <a href="/#join" className="bc-link" data-testid="nav-join">
              Join
            </a>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
             /*  href={buildWhatsAppLink(
                "Hello Believers Common, I'd like to register my interest in joining the founding cohort."
              )}
              target="_blank" */
              href="/#join"
              rel="noreferrer"
              className="btn-primary"
              data-testid="header-whatsapp-cta"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.6} />
              Enquire Now
            </a>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-white/10"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* mobile menu */}
        {open && (
          <div
            className="md:hidden border-t border-white/5 bg-[#0A0D14]/95 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5 text-[13px] tracking-[0.18em] uppercase">
              <Link to="/" className="bc-link" data-testid="m-nav-home">
                Home
              </Link>
              <Link to="/about" className="bc-link" data-testid="m-nav-about">
                About
              </Link>
              <a href="/#what-we-do" className="bc-link" data-testid="m-nav-services">
                Services
              </a>
              <a href="/#how-it-works" className="bc-link" data-testid="m-nav-how">
                How it works
              </a>
              <a href="/#join" className="bc-link" data-testid="m-nav-join">
                Join the waiting list
              </a>
              <a
                href={buildWhatsAppLink("Hello Believers Common")}
                target="_blank"
                rel="noreferrer"
                className="btn-primary mt-3 justify-center"
                data-testid="m-whatsapp-cta"
              >
                <MessageCircle className="w-4 h-4" /> Message on WhatsApp
              </a>
            </div>
          </div>
        )}
      </header>

      <main className="relative z-10">
        <Outlet />
      </main>

      <Footer />

      {/* Sticky WhatsApp floating CTA */}
      <a
        href="/#join"
        rel="noreferrer"
        className="fixed bottom-[68px] sm:bottom-20 md:bottom-6 left-4 sm:left-auto right-auto sm:right-4 md:right-[210px] z-50 group"
        data-testid="sticky-whatsapp-cta"
        aria-label="Message Believers Common on WhatsApp"
      >
        <span className="absolute inset-0 rounded-full bg-[#1F7A5A]/40 blur-xl group-hover:bg-[#1F7A5A]/60 transition-all" />
        <span className="relative flex items-center gap-3 pl-4 pr-5 py-3 backdrop-blur-xl bg-[#0A0D14]/80 border border-[#1F7A5A]/40 rounded-full hover:border-[#58D2C7]/60 transition-all">
          <span className="relative flex w-8 h-8 items-center justify-center rounded-full bg-[#1F7A5A]">
            <MessageCircle className="w-4 h-4 text-[#F6F7FB]" strokeWidth={1.8} />
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#58D2C7] bc-pulse" />
          </span>
          <span className="text-[12px] tracking-[0.22em] uppercase text-[#F6F7FB]">
             Join
          </span>
        </span>
      </a>
    </div>
  );
};

const NavLink = ({ to, current, label, testid }) => (
  <Link
    to={to}
    className={`bc-link ${current ? "text-[#D6B25E]" : ""}`}
    data-testid={testid}
  >
    {label}
  </Link>
);

const BrandMark = () => (
  <svg width="42" height="42" viewBox="0 0 42 42" className="shrink-0">
    <circle cx="21" cy="21" r="18" fill="none" stroke="#D6B25E" strokeWidth="1" />
    <circle cx="21" cy="21" r="11" fill="none" stroke="#58D2C7" strokeWidth="1" />
    <circle cx="21" cy="21" r="3.5" fill="#D6B25E" />
    <line x1="3" y1="21" x2="9" y2="21" stroke="rgba(246,247,251,0.4)" strokeWidth="1" />
    <line x1="33" y1="21" x2="39" y2="21" stroke="rgba(246,247,251,0.4)" strokeWidth="1" />
    <line x1="21" y1="3" x2="21" y2="9" stroke="rgba(246,247,251,0.4)" strokeWidth="1" />
    <line x1="21" y1="33" x2="21" y2="39" stroke="rgba(246,247,251,0.4)" strokeWidth="1" />
  </svg>
);

const Footer = () => {
  return (
    <footer
      className="relative z-10 mt-20 md:mt-24 border-t border-white/5 pt-16 md:pt-20 pb-10 px-5 sm:px-6 lg:px-10"
      data-testid="site-footer"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="eyebrow-slate mb-5">A Trusted Common</div>
          <h3 className="font-serif-display text-4xl md:text-5xl leading-[1.05] mb-6">
            Where people, service,
            <br />
            and accountability <span className="italic text-[#D6B25E]">meet</span>.
          </h3>
          <p className="text-[#6F7787] max-w-md leading-relaxed">
            Believers Common is being developed from Karnataka, India, as a
            physical–digital commons for verified identity, accountable service,
            and community-led prosperity.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow-slate mb-5">Navigate</div>
          <ul className="space-y-3 text-[14px]">
            <li>
              <Link to="/" className="bc-link" data-testid="footer-home">
                Home
              </Link>
            </li>
            <li>
              <Link to="/about" className="bc-link" data-testid="footer-about">
                About
              </Link>
            </li>
            <li>
              <a href="/#what-we-do" className="bc-link">
                What we do
              </a>
            </li>
            <li>
              <a href="/#how-it-works" className="bc-link">
                How it works
              </a>
            </li>
            <li>
              <a href="/#join" className="bc-link">
                Join the waiting list
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="eyebrow-slate mb-5">Reach</div>
          <ul className="space-y-3 text-[14px]">
            <li className="text-[#6F7787]">
              Karnataka, India · Physical & Digital Commons
            </li>
            <li>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noreferrer"
                className="bc-link"
                data-testid="footer-whatsapp"
              >
                WhatsApp · +91 96864 54696
              </a>
            </li>
            <li>
              <a href="mailto:faiz@believerscommon.com" className="bc-link">
                faiz@believerscommon.com
              </a>
            </li>
          </ul>

          <div className="mt-8 flex items-center gap-2">
            {[
              { abbr: "IG", label: "Instagram" },
              { abbr: "LI", label: "LinkedIn" },
              
            ].map((s) => (
              <a
                key={s.abbr}
                href={s.label === "WhatsApp" ? `https://wa.me/${WHATSAPP_NUMBER}` : "#"}
                target="_blank"
                rel="noreferrer"
                aria-label={s.label}
                className="w-10 h-10 inline-flex items-center justify-center border border-white/10 hover:border-[#D6B25E] hover:text-[#D6B25E] transition-colors text-[11px] tracking-[0.18em]"
                data-testid={`footer-social-${s.abbr.toLowerCase()}`}
              >
                {s.abbr}
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="bc-divider mt-16" />

      <div className="max-w-[1400px] mx-auto mt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[11px] tracking-[0.22em] uppercase text-[#6F7787]">
        <div>
          © {new Date().getFullYear()} Believers Common · Mission-led Initiative
        </div>
        <div className="flex items-center gap-6">
          <a href="#privacy" className="hover:text-[#D6B25E]">
            Privacy
          </a>
          <a href="#terms" className="hover:text-[#D6B25E]">
            Terms
          </a>
          <span>
            Registration & operating structure being formalized
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Layout;
